import {
  Box,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { Button } from 'src/components/atoms/Button';
import {
  HONEYPOT_FIELD_NAME,
  HoneypotInput,
} from 'src/components/atoms/HoneypotInput';
import { useContactForm } from 'src/hooks/useContactForm';
import { TURNSTILE_ACTIONS, useTurnstile } from 'src/hooks/useTurnstile';

type ContactFormData = {
  name: string;
  email: string;
  company: string;
  message: string;
  // Honeypot — see HoneypotInput. Lives in form state so all fields share RHF.
  website_url: string;
};

type ContactFormProps = {
  onClose?: () => void;
  defaultMessage?: string;
  submitButtonText?: string;
};

export const ContactForm: React.FC<ContactFormProps> = ({
  onClose,
  defaultMessage = "We'd like to discuss pricing, available plans, or schedule a guided demo of the platform.",
  submitButtonText = 'Send Message',
}) => {
  const { submitContactForm, isLoading } = useContactForm();
  const captcha = useTurnstile(TURNSTILE_ACTIONS.CONTACT);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<ContactFormData>({
    defaultValues: {
      name: '',
      email: '',
      company: '',
      message: defaultMessage,
      website_url: '',
    },
  });

  const handleFormSubmit = async (data: ContactFormData) => {
    if (!captcha.token) return;

    const result = await submitContactForm({
      name: data.name,
      email: data.email,
      company: data.company,
      message: data.message,
      captchaToken: captcha.token,
      honeypot: data.website_url,
    });
    if (result.success) {
      reset();
      onClose?.();
      return;
    }
    captcha.reset();
  };

  const submitDisabled = isLoading || !captcha.token;
  const submitLabel = !captcha.ready
    ? 'Loading verification…'
    : !captcha.token
      ? 'Complete verification'
      : submitButtonText;

  return (
    <Box as="form" onSubmit={handleSubmit(handleFormSubmit)}>
      <VStack spacing={6} align="stretch">
        <FormControl isInvalid={!!errors.name}>
          <FormLabel htmlFor="name" fontSize="base" fontWeight="medium">
            Name *
          </FormLabel>
          <Input
            {...register('name', { required: 'Name is required' })}
            id="name"
            placeholder="John Doe"
            maxLength={100}
            bg="gray.50"
            borderColor="transparent"
            borderRadius="md"
            _placeholder={{ color: 'gray.400' }}
            _focus={{
              borderColor: 'blue.500',
              boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)',
            }}
            _hover={{
              borderColor: 'gray.300',
            }}
          />
          {errors.name && (
            <FormErrorMessage>{errors.name.message}</FormErrorMessage>
          )}
        </FormControl>

        <FormControl isInvalid={!!errors.email}>
          <FormLabel htmlFor="email" fontSize="base" fontWeight="medium">
            Email *
          </FormLabel>
          <Input
            {...register('email', {
              required: 'Email is required',
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: 'Invalid email address',
              },
            })}
            id="email"
            type="email"
            placeholder="john.doe@company.com"
            maxLength={255}
            bg="gray.50"
            borderColor="transparent"
            borderRadius="md"
            _placeholder={{ color: 'gray.400' }}
            _focus={{
              borderColor: 'blue.500',
              boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)',
            }}
            _hover={{
              borderColor: 'gray.300',
            }}
          />
          {errors.email && (
            <FormErrorMessage>{errors.email.message}</FormErrorMessage>
          )}
        </FormControl>

        <FormControl isInvalid={!!errors.company}>
          <FormLabel htmlFor="company" fontSize="base" fontWeight="medium">
            Company *
          </FormLabel>
          <Input
            {...register('company', { required: 'Company name is required' })}
            id="company"
            placeholder="Your Company"
            maxLength={200}
            bg="gray.50"
            borderColor="transparent"
            borderRadius="md"
            _placeholder={{ color: 'gray.400' }}
            _focus={{
              borderColor: 'blue.500',
              boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)',
            }}
            _hover={{
              borderColor: 'gray.300',
            }}
          />
          {errors.company && (
            <FormErrorMessage>{errors.company.message}</FormErrorMessage>
          )}
        </FormControl>

        <FormControl isInvalid={!!errors.message}>
          <FormLabel htmlFor="message" fontSize="base" fontWeight="medium">
            Message *
          </FormLabel>
          <Textarea
            {...register('message', { required: 'Message is required' })}
            id="message"
            placeholder="Tell us about your needs..."
            bg="gray.50"
            borderColor="transparent"
            borderRadius="md"
            _placeholder={{ color: 'gray.400' }}
            _focus={{
              borderColor: 'blue.500',
              boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)',
            }}
            _hover={{
              borderColor: 'gray.300',
            }}
            rows={4}
            maxLength={1000}
          />
          {errors.message && (
            <FormErrorMessage>{errors.message.message}</FormErrorMessage>
          )}
          <Text fontSize="xs" color="gray.400" textAlign="right" mt={1}>
            {watch('message')?.length ?? 0} / 1000
          </Text>
        </FormControl>

        <HoneypotInput {...register(HONEYPOT_FIELD_NAME)} />

        <Box ref={captcha.containerRef} />

        <Box display="flex" justifyContent="center" pt={4}>
          <Button
            buttonName="contact-form-submit"
            type="submit"
            size="lg"
            bg={'icon.green'}
            color="white"
            fontFamily="marketingPage"
            borderRadius={8}
            px={8}
            py={4}
            isLoading={isLoading}
            loadingText="Sending..."
            isDisabled={submitDisabled}
            _hover={{ bg: 'green.600' }}>
            {submitLabel} &nbsp; →
          </Button>
        </Box>
      </VStack>
    </Box>
  );
};
